/**
 * Qué cara del DNI trae una foto, por lo que la lectura ya ve.
 *
 * Recorrido del usuario: en el paso del frente cargó una foto del dorso y la
 * pantalla no se dio cuenta. La cascada lee la foto como la cara del paso, y un
 * dorso leído como frente da igual los datos del MRZ: nada lo frenaba.
 *
 * Las señales, en orden de peso:
 *  - el PDF417 con la forma del frente 2012 (empieza por el trámite): frente;
 *  - el QR del 2026, que va en el dorso: dorso;
 *  - el MRZ: dorso, salvo que también estén los rótulos del frente, que es el
 *    diseño 2009-2012, con el MRZ adelante;
 *  - los rótulos de cada cara (el frente: apellido, nombre, sexo, fechas; el
 *    dorso: domicilio, lugar de nacimiento, CUIL), por mayoría.
 * Sin señales, o empatadas, no se afirma nada.
 *
 * Una foto con las DOS caras que no se separó trae señales de las dos, y no es
 * «la otra cara» (revisión del recorrido 3): el PDF417 del frente 2012 con un MRZ
 * (el 2012 no lo lleva adelante), el QR del dorso 2026 con rótulos del frente, o
 * rótulos de las dos caras. Se reconoce como "ambas".
 */
import type { Cara } from "./catalogo";

export interface SenalesDeCara {
  codigoDelFrente: boolean;
  /** Un código 2D que el catálogo declara en el dorso (el PDF417 del dorso 2009, que empieza con «@»). */
  codigoDelDorso: boolean;
  qrDelDorso: boolean;
  mrz: boolean;
  /** Cuántos datos del frente se leyeron bajo sus rótulos. */
  rotulosDelFrente: number;
  /** Cuántos datos del dorso se leyeron junto a sus rótulos. */
  rotulosDelDorso: number;
}

/** Con cuántos datos del frente un MRZ es el del frente 2009 y no el de un dorso. */
const FRENTE_CON_MRZ = 2;

/** Cuántos rótulos de cada cara hacen falta para decir que la foto trae las dos. */
const ROTULOS_DE_UNA_CARA = 2;

export type CaraDeLaFoto = Cara | "ambas";

export function caraPorSenales(s: SenalesDeCara): CaraDeLaFoto | null {
  if (s.codigoDelFrente && s.mrz) return "ambas";
  if (s.codigoDelDorso && (s.codigoDelFrente || (s.mrz && s.rotulosDelFrente >= ROTULOS_DE_UNA_CARA))) return "ambas";
  if (s.qrDelDorso && s.rotulosDelFrente >= ROTULOS_DE_UNA_CARA) return "ambas";
  if (s.rotulosDelFrente >= ROTULOS_DE_UNA_CARA && s.rotulosDelDorso >= ROTULOS_DE_UNA_CARA) return "ambas";
  if (s.codigoDelFrente) return "frente";
  if (s.codigoDelDorso) return "dorso";
  if (s.qrDelDorso) return "dorso";
  if (s.mrz) return s.rotulosDelFrente >= FRENTE_CON_MRZ && s.rotulosDelFrente > s.rotulosDelDorso ? "frente" : "dorso";
  if (s.rotulosDelDorso > s.rotulosDelFrente) return "dorso";
  if (s.rotulosDelFrente > s.rotulosDelDorso) return "frente";
  return null;
}
